import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import publicApi from "@/lib/publicApi";
import usePageContent, { s } from "@/hooks/usePageContent";
import { localePath, parseLocaleFromPath } from "@/lib/locale";
import { ProductLD, BreadcrumbLD } from "@/components/StructuredData";
import CatalogueCallout from "@/components/CatalogueCallout";
import ProductInquiryForm from "@/components/ProductInquiryForm";
import useSiteConfig from "@/hooks/useSiteConfig";

// URL slug → product type id used for JSON-LD lookup + product API.
const SLUG_TO_PRODUCT_ID = {
  primovi: "prim",
  bisernice: "bisernica",
  bracevi: "brac",
  bugarije: "bugarija",
  cela: "celo",
  berde: "berda",
};

export default function InstrumentPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { locale, pathWithoutLocale } = parseLocaleFromPath(location.pathname);
  const slug = pathWithoutLocale.replace(/^\/+|\/+$/g, "").split("/").pop();
  const { data: page } = usePageContent(slug, locale);
  const [product, setProduct] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const productId = SLUG_TO_PRODUCT_ID[slug] || slug;
  const cfg = useSiteConfig();
  const commerceOn = !!cfg.commerce_mode_enabled;

  useEffect(() => {
    if (!slug) return;
    publicApi.get(`/public/product-types/${slug}`, { params: { locale } }).then((r) => setProduct(r.data)).catch(() => setProduct(null));
  }, [slug, locale]);

  const PARA_KEYS = ["p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8"];

  return (
    <>
      <SEO
        title={page?.meta_title || `${product?.name || ""} — Tambure Žmegač`}
        description={page?.meta_description || product?.description}
        image={product?.hero_image_url ? `${origin}${product.hero_image_url}` : undefined}
      />
      {product && (
        <>
          <ProductLD
            origin={origin}
            product={{ ...product, id: productId }}
            locale={locale}
            canonicalUrl={`${origin}${location.pathname}`}
          />
          <BreadcrumbLD
            origin={origin}
            items={[
              { name: t("nav.home"), url: localePath(locale, "/") },
              { name: t("nav.models"), url: localePath(locale, "/models") },
              { name: product.name, url: location.pathname },
            ]}
          />
        </>
      )}
      <article className="bg-background">
        <section className="relative bg-tz-espresso text-tz-cream overflow-hidden">
          {product?.hero_image_url && (
            <img src={product.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="eager" />
          )}
          <div className="absolute inset-0 bg-gradient-to-tr from-tz-espresso via-tz-espresso/85 to-tz-espresso/50" />
          <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-32">
            <Link to={localePath(locale, "/models")} data-testid="instrument-back-link" className="inline-flex items-center gap-1.5 text-sm text-tz-cream/70 hover:text-primary mb-8 transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("actions.back")}
            </Link>
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4" data-testid="instrument-tagline">{s(page, "tagline") || product?.tagline}</div>
            <h1 className="font-serif text-6xl lg:text-8xl leading-[0.95] tracking-tight" data-testid="instrument-name">
              {product?.name || slug}
            </h1>
          </div>
        </section>

        <section className="bg-card border-y border-border/60">
          <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
            <div className="rounded-lg overflow-hidden border border-border/60 shadow-ambient bg-background">
              {product?.hero_image_url && (
                <img src={product.hero_image_url} alt={product.name} loading="lazy" className="w-full h-auto object-cover" data-testid="instrument-image" />
              )}
            </div>
            <div className="space-y-5 text-foreground/85 text-lg leading-relaxed" data-testid="instrument-intro">
              <RichContent
                html={s(page, "p1")}
                className="text-xl first-letter:font-serif first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-primary"
              />
              <RichContent html={s(page, "p2")} />
            </div>
          </div>
        </section>

        <section className="bg-background grain-bg">
          <div className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-24 space-y-6 text-foreground/85 text-lg leading-relaxed" data-testid="instrument-longform">
            {PARA_KEYS.slice(2).map((k) => {
              const v = s(page, k);
              if (!v) return null;
              return <RichContent key={k} html={v} />;
            })}
          </div>
        </section>

        {/* CTA — commerce OFF: inquiry-first form with callout. Commerce ON: legacy contact buttons. */}
        {!commerceOn ? (
          <section className="bg-tz-espresso/[0.04] border-t border-border/60">
            <div className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-24 space-y-6">
              <CatalogueCallout testid="instrument-catalogue-callout" />
              {product && <ProductInquiryForm product={product} locale={locale} />}
            </div>
          </section>
        ) : (
          <section className="bg-tz-espresso text-tz-cream">
            <div className="max-w-4xl mx-auto px-5 lg:px-10 py-16 text-center">
              <h2 className="font-serif text-3xl lg:text-4xl mb-4">{t("actions.inquire")}</h2>
              <p className="text-tz-cream/75 mb-8 max-w-xl mx-auto">{product?.tagline}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to={localePath(locale, "/contact")} data-testid="instrument-cta-contact" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-3 text-sm font-medium shadow-interactive hover:bg-primary/90 transition-colors">
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> {t("actions.contact_us")}
                </Link>
                <Link to={localePath(locale, "/models")} data-testid="instrument-cta-models" className="inline-flex items-center gap-2 rounded-lg border border-tz-cream/30 px-5 py-3 text-sm font-medium hover:bg-white/5 transition-colors">
                  {t("actions.view_all")}
                </Link>
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}
